import { supabase } from '@/integrations/supabase/client';

export interface WarehouseAdjustmentData {
  received_quantity_kg: number;
  net_kg_payable: number;
}

export interface QualityAdjustmentData {
  damage_kg: number;
  compensation_kg: number;
}

export interface ItemWarehouseAdjustmentData {
  received_quantity_kg: number;
  net_payable_kg: number;
}

export interface ItemQualityAdjustmentData {
  damage_kg: number;
  compensation_kg: number;
}

export interface AdjustmentAuditData {
  procurement_id: string;
  action: string;
  old_values: Record<string, any>;
  new_values: Record<string, any>;
  change_reason: string;
  changed_by: string;
}

/**
 * Validates that the current user is an admin
 */
async function validateAdminRole(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (error || !profile) {
    throw new Error('Could not verify user role');
  }

  if (profile.role !== 'admin') {
    throw new Error('Only administrators can make adjustments');
  }

  return user.id;
}

/**
 * Validates that the procurement is in pending_finance status
 */
async function validateProcurementStatus(procurementId: string): Promise<void> {
  const { data: procurement, error } = await supabase
    .from('procurements')
    .select('status')
    .eq('id', procurementId)
    .single();

  if (error || !procurement) {
    throw new Error('Procurement not found');
  }

  if (procurement.status !== 'pending_finance') {
    throw new Error('Adjustments can only be made when procurement is pending finance approval');
  }
}

/**
 * Logs adjustment to procurement_audit table
 */
async function logAuditTrail(data: AdjustmentAuditData): Promise<void> {
  const { error } = await supabase
    .from('procurement_audit')
    .insert({
      procurement_id: data.procurement_id,
      action: data.action,
      changed_by: data.changed_by,
      change_reason: data.change_reason,
      metadata: {
        old_values: data.old_values,
        new_values: data.new_values,
        adjustment_type: 'admin_adjustment'
      }
    });

  if (error) {
    console.error('Failed to log audit trail:', error);
    // Don't throw - audit failure shouldn't block the adjustment
  }
}

/**
 * Update warehouse data for a procurement
 */
export async function updateWarehouseData(
  procurementId: string,
  data: WarehouseAdjustmentData,
  reason: string
): Promise<void> {
  const adminId = await validateAdminRole();
  await validateProcurementStatus(procurementId);

  // Get current values for audit
  const { data: currentWarehouse, error: fetchError } = await supabase
    .from('procurement_warehouse')
    .select('received_quantity_kg, net_kg_payable')
    .eq('procurement_id', procurementId)
    .single();

  if (fetchError) {
    throw new Error('Could not fetch current warehouse data');
  }

  // Update warehouse data
  const { error: updateError } = await supabase
    .from('procurement_warehouse')
    .update({
      received_quantity_kg: data.received_quantity_kg,
      net_kg_payable: data.net_kg_payable,
      updated_at: new Date().toISOString()
    })
    .eq('procurement_id', procurementId);

  if (updateError) {
    throw new Error(`Failed to update warehouse data: ${updateError.message}`);
  }

  // Log audit trail
  await logAuditTrail({
    procurement_id: procurementId,
    action: 'admin_warehouse_adjustment',
    old_values: currentWarehouse || {},
    new_values: data,
    change_reason: reason,
    changed_by: adminId
  });
}

/**
 * Update quality data for a procurement
 * Also recalculates net_kg_payable in warehouse when compensation changes
 */
export async function updateQualityData(
  procurementId: string,
  data: QualityAdjustmentData,
  reason: string
): Promise<void> {
  const adminId = await validateAdminRole();
  await validateProcurementStatus(procurementId);

  // Get current values for audit
  const { data: currentQuality, error: fetchError } = await supabase
    .from('procurement_quality')
    .select('damage_kg, compensation_kg')
    .eq('procurement_id', procurementId)
    .single();

  if (fetchError) {
    throw new Error('Could not fetch current quality data');
  }

  // Update quality data
  const { error: updateError } = await supabase
    .from('procurement_quality')
    .update({
      damage_kg: data.damage_kg,
      compensation_kg: data.compensation_kg,
      updated_at: new Date().toISOString()
    })
    .eq('procurement_id', procurementId);

  if (updateError) {
    throw new Error(`Failed to update quality data: ${updateError.message}`);
  }

  // Recalculate net_kg_payable in warehouse based on new compensation
  const { data: warehouseData, error: whFetchError } = await supabase
    .from('procurement_warehouse')
    .select('received_quantity_kg, net_kg_payable')
    .eq('procurement_id', procurementId)
    .single();

  if (!whFetchError && warehouseData) {
    const newNetPayable = Math.max(0, (warehouseData.received_quantity_kg || 0) - data.compensation_kg);
    if (warehouseData.net_kg_payable !== newNetPayable) {
      const { error: whUpdateError } = await supabase
        .from('procurement_warehouse')
        .update({
          net_kg_payable: newNetPayable,
          updated_at: new Date().toISOString()
        })
        .eq('procurement_id', procurementId);

      if (whUpdateError) {
        console.error('Failed to recalculate net_kg_payable:', whUpdateError);
      }
    }
  }

  // Log audit trail
  await logAuditTrail({
    procurement_id: procurementId,
    action: 'admin_quality_adjustment',
    old_values: currentQuality || {},
    new_values: data,
    change_reason: reason,
    changed_by: adminId
  });
}

/**
 * Update item-level warehouse data
 */
export async function updateItemWarehouseData(
  procurementId: string,
  itemId: string,
  data: ItemWarehouseAdjustmentData,
  reason: string
): Promise<void> {
  const adminId = await validateAdminRole();
  await validateProcurementStatus(procurementId);

  // Get current values for audit
  const { data: currentItemWarehouse, error: fetchError } = await supabase
    .from('procurement_item_warehouse')
    .select('received_quantity_kg, net_payable_kg')
    .eq('procurement_item_id', itemId)
    .single();

  if (fetchError) {
    throw new Error('Could not fetch current item warehouse data');
  }

  // Update item warehouse data
  const { error: updateError } = await supabase
    .from('procurement_item_warehouse')
    .update({
      received_quantity_kg: data.received_quantity_kg,
      net_payable_kg: data.net_payable_kg,
      updated_at: new Date().toISOString()
    })
    .eq('procurement_item_id', itemId);

  if (updateError) {
    throw new Error(`Failed to update item warehouse data: ${updateError.message}`);
  }

  // Log audit trail
  await logAuditTrail({
    procurement_id: procurementId,
    action: 'admin_item_warehouse_adjustment',
    old_values: { item_id: itemId, ...currentItemWarehouse },
    new_values: { item_id: itemId, ...data },
    change_reason: reason,
    changed_by: adminId
  });
}

/**
 * Update item-level quality data
 */
export async function updateItemQualityData(
  procurementId: string,
  itemId: string,
  data: ItemQualityAdjustmentData,
  reason: string
): Promise<void> {
  const adminId = await validateAdminRole();
  await validateProcurementStatus(procurementId);

  // Get current values for audit
  const { data: currentItemQuality, error: fetchError } = await supabase
    .from('procurement_item_quality')
    .select('damage_kg, compensation_kg')
    .eq('procurement_item_id', itemId)
    .single();

  if (fetchError) {
    throw new Error('Could not fetch current item quality data');
  }

  // Update item quality data
  const { error: updateError } = await supabase
    .from('procurement_item_quality')
    .update({
      damage_kg: data.damage_kg,
      compensation_kg: data.compensation_kg,
      updated_at: new Date().toISOString()
    })
    .eq('procurement_item_id', itemId);

  if (updateError) {
    throw new Error(`Failed to update item quality data: ${updateError.message}`);
  }

  // Log audit trail
  await logAuditTrail({
    procurement_id: procurementId,
    action: 'admin_item_quality_adjustment',
    old_values: { item_id: itemId, ...currentItemQuality },
    new_values: { item_id: itemId, ...data },
    change_reason: reason,
    changed_by: adminId
  });
}

/**
 * Combined update for both warehouse and quality data
 */
export async function updateProcurementAdjustments(
  procurementId: string,
  warehouseData: WarehouseAdjustmentData | null,
  qualityData: QualityAdjustmentData | null,
  reason: string
): Promise<void> {
  if (warehouseData) {
    await updateWarehouseData(procurementId, warehouseData, reason);
  }
  if (qualityData) {
    await updateQualityData(procurementId, qualityData, reason);
  }
}
